import { z } from 'zod';
import { contactInfoShape, isValidPassword } from './ContactInfoStep';

/**
 * Creating an account: one schema for the whole wizard, in three slices,
 * shared by the dashboard's /onboarding and the phone's sign-up so the two
 * ask the same questions and refuse the same answers.
 *
 * A step validates only its own slice — `ONBOARDING_STEP_FIELDS` names them
 * and `form.trigger` is given that list — so step 1 never fails on an empty
 * step 3. The final submit runs the whole object, which is what makes the
 * three slices one type rather than three forms that happen to agree.
 */
const contactSlice = {
  ...contactInfoShape,
  password: contactInfoShape.password.refine(
    isValidPassword,
    'Your password does not meet the requirements above'
  ),
};

const companySlice = {
  companyName: z.string().trim().min(1, 'Enter your company name'),
  companyAddress: z.string().trim().min(1, 'Enter your company address'),
  companyStreet: z.string(),
  companyCity: z.string(),
  companyState: z.string(),
  companyZip: z.string(),
  companySize: z.string().min(1, 'Choose a company size'),
  averageCallsPerMonth: z.string().min(1, 'Choose an average'),
};

const planSlice = {
  selectedPlan: z.string().min(1, 'Choose a plan to continue'),
};

const passwordsMatch = (v: { password: string; confirmPassword: string }) =>
  v.password === v.confirmPassword;
const MATCH_ISSUE = {
  path: ['confirmPassword'],
  message: 'Passwords do not match',
};

export const onboardingSchema = z
  .object({ ...contactSlice, ...companySlice, ...planSlice })
  .refine(passwordsMatch, MATCH_ISSUE);

export type OnboardingValues = z.infer<typeof onboardingSchema>;

export type OnboardingStep = 1 | 2 | 3;

/** The three steps, in order, with the heading each one opens under. */
export const ONBOARDING_STEPS: {
  id: OnboardingStep;
  title: string;
  heading: string;
}[] = [
  {
    id: 1,
    title: 'Contact info',
    heading: "Let's start with your contact information",
  },
  {
    id: 2,
    title: 'Company info',
    heading: 'Tell us about your towing business',
  },
  {
    id: 3,
    title: 'Choose plan',
    heading: 'Choose the perfect plan for your business',
  },
];

/**
 * The same three slices as standalone schemas, so a step indicator can ask
 * "is this step done?" without awaiting a `trigger`.
 */
export const ONBOARDING_STEP_SCHEMAS: Record<OnboardingStep, z.ZodTypeAny> = {
  1: z.object(contactSlice).refine(passwordsMatch, MATCH_ISSUE),
  2: z.object(companySlice),
  3: z.object(planSlice),
};

/** Which fields each step is allowed to fail on. */
export const ONBOARDING_STEP_FIELDS: Record<
  OnboardingStep,
  (keyof OnboardingValues)[]
> = {
  1: Object.keys(contactSlice) as (keyof OnboardingValues)[],
  2: Object.keys(companySlice) as (keyof OnboardingValues)[],
  3: Object.keys(planSlice) as (keyof OnboardingValues)[],
};

export const EMPTY_ONBOARDING: OnboardingValues = {
  fullName: '',
  email: '',
  phoneNumber: '',
  password: '',
  confirmPassword: '',
  companyName: '',
  companyAddress: '',
  companyStreet: '',
  companyCity: '',
  companyState: '',
  companyZip: '',
  companySize: '',
  averageCallsPerMonth: '',
  selectedPlan: '',
};

/** The company sizes offered, the values the server's DTO names. */
export const COMPANY_SIZE_OPTIONS = [
  { value: '1-4', label: '1-4 employees' },
  { value: '5-9', label: '5-9 employees' },
  { value: '10+', label: '10+ employees' },
];

export const CALLS_PER_MONTH_OPTIONS = [
  { value: '0-50', label: '0-50 calls' },
  { value: '51-100', label: '51-100 calls' },
  { value: '101-200', label: '101-200 calls' },
  { value: '201+', label: '201+ calls' },
];

/**
 * What `POST /onboarding/register` is sent, from the wizard's values. One
 * function so the dashboard and the phone send the same body; the sales
 * page a visitor came from is the dashboard's alone, and left out when
 * undefined.
 */
export function onboardingRequestBody(
  values: OnboardingValues,
  salesRef?: string
) {
  return {
    fullName: values.fullName,
    email: values.email,
    phoneNumber: values.phoneNumber,
    password: values.password,
    confirmPassword: values.confirmPassword,
    companyName: values.companyName,
    companyAddress: values.companyAddress,
    companyStreet: values.companyStreet,
    companyCity: values.companyCity,
    companyState: values.companyState,
    companyZip: values.companyZip,
    companySize: values.companySize,
    averageCallsPerMonth: values.averageCallsPerMonth,
    selectedPlan: values.selectedPlan,
    salesRef,
  };
}
